/**
 * Snowbird recreational transport — second-tier city pages.
 *
 * These are markets that send or receive real seasonal equipment volume but
 * sit a step below the pilot group in size. Each entry is still written for
 * that specific market: what people there actually ship, the corridors that
 * matter, the seasonal timing and the local pickup/delivery realities. Nothing
 * here is a template fill-in, and no statistics are invented.
 */

import type { SnowbirdCity } from "./snowbird-cities";

export const TIER2_CITIES: SnowbirdCity[] = [
  {
    slug: "rochester",
    name: "Rochester",
    stateName: "New York",
    stateSlug: "new-york",
    role: "origin",
    title: "Snowbird Toy Transport from Rochester, NY | Enterprise Auto Transport",
    description:
      "Seasonal boat, RV, camper and golf cart transport out of Rochester and the Finger Lakes to Florida, the Carolinas and Arizona — timing, storage pickups and trailer prep.",
    short: "Finger Lakes boats, campers and carts heading south before the lake-effect season.",
    lede: [
      "Rochester equipment usually spends the summer on Lake Ontario, Canandaigua, Keuka or Seneca and comes off the water in a hurry once October turns. The shipping decision here is almost always tied to a haul-out date at a marina rather than to a date at the owner's house.",
      "Enterprise Auto Transport is a licensed broker. We arrange the move with a qualified motor carrier whose equipment matches your boat, trailer or RV — we do not own trucks or employ drivers.",
    ],
    context: [
      {
        heading: "Haul-out schedules drive pickup dates",
        body: "Most Rochester-area boats are shipped straight out of a marina or a yard after haul-out and shrink-wrap season begins. Marinas run tight lift schedules in October, so the practical order is: book the haul-out, confirm the date, then book transport for a two- or three-day window after it.",
      },
      {
        heading: "Lake-effect weather is a scheduling risk, not just a comfort issue",
        body: "Once serious lake-effect snow starts, an open yard can become impossible to load from and a driver may not be able to get a 70-foot rig turned around. Shipping before Thanksgiving is materially easier than shipping after it.",
      },
      {
        heading: "Storage yards spread across the suburbs",
        body: "Equipment often lives in Greece, Webster, Henrietta, Victor or out toward Canandaigua rather than at the owner's address. Those yards usually load well, but they need gate access, a release authorization on file and someone reachable by phone at pickup.",
      },
    ],
    gear: [
      {
        name: "Runabouts and cruisers off Lake Ontario",
        note: "Common in the 21–32 ft range. Overall length has to include the swim platform and the outboard or bracket, which is where quotes most often get corrected.",
        equipment: "boat-transport",
      },
      {
        name: "Travel trailers and small fifth wheels",
        note: "Owners frequently ship the trailer south for the season instead of towing it themselves through the Appalachians in winter weather.",
        equipment: "travel-trailer-transport",
      },
      {
        name: "Golf carts",
        note: "Carts bound for Florida communities usually move with other freight; battery type and whether it rolls freely both matter to the loading plan.",
        equipment: "golf-cart-transport",
      },
      {
        name: "Motorcycles and touring bikes",
        note: "Riders who spend the winter in the Carolinas or Florida generally ship the bike rather than ride it out in November.",
        equipment: "motorcycle-transport",
      },
    ],
    routes: [
      {
        label: "Rochester → Tampa / Sarasota",
        note: "The most requested lane from here. I-90 to I-79 or I-81 southbound; carriers combine Western New York loads with Pennsylvania and Ohio pickups.",
      },
      {
        label: "Rochester → Naples / Fort Myers",
        note: "Southwest Florida delivery usually means a storage lot or a gated community, so confirm the receiving address before dispatch, not after.",
      },
      {
        label: "Rochester → Myrtle Beach / Charleston",
        note: "A shorter run that fills quickly in October; a shorter lane does not mean a faster dispatch when everyone wants the same week.",
      },
      {
        label: "Rochester → Phoenix / Tucson",
        note: "A long-haul lane with fewer trucks. Expect a wider pickup window and book earlier than you would for a Florida move.",
      },
    ],
    timing: [
      "Book three to six weeks ahead for October–December departures; the last two weeks of October are the tightest window of the year here.",
      "Spring returns from Florida to Rochester run March through May, and the March rush is heavier than most owners expect.",
      "If your haul-out slips, tell us as soon as you know — moving a scheduled pickup is far easier than recovering a missed one.",
    ],
    logistics: [
      "Marina pickups need the yard's gate hours in writing; several local yards close early on weekends in shoulder season.",
      "Shrink wrap can raise overall height enough to change permitting. Tell us if the boat will be wrapped before it ships.",
      "Winterize before pickup. Water left in a system can freeze on the first cold night of the trip, and that damage is not a transport claim.",
      "If the unit rolls on its own tires, the tires, bearings, brakes, lights and coupler must be roadworthy at pickup; towing and repairs on the road are the owner's cost.",
    ],
    faqs: [
      {
        q: "Can you pick up directly from my marina in the Rochester area?",
        a: "Yes, as long as the yard allows carrier access and has room to maneuver. Give us the gate hours and a contact who can authorize release, especially if you will already be in Florida.",
      },
      {
        q: "Is it cheaper to ship in September than in late October?",
        a: "Usually, yes, because you are not competing with the whole region for the same trucks. If your haul-out can be scheduled early, that is the simplest way to reduce both cost and pickup-window risk.",
      },
      {
        q: "Do you handle the return trip in the spring?",
        a: "Yes. Most Rochester customers book the return in February or March for an April or May pickup in Florida.",
      },
    ],
    equipment: [
      "boat-transport",
      "travel-trailer-transport",
      "golf-cart-transport",
      "motorcycle-transport",
    ],
  },
  {
    slug: "buffalo",
    name: "Buffalo",
    stateName: "New York",
    stateSlug: "new-york",
    role: "origin",
    title: "Snowbird Toy Transport from Buffalo, NY | Enterprise Auto Transport",
    description:
      "Boat, RV, trailer and powersports transport out of Buffalo and Western New York to Florida, the Gulf Coast and Arizona for the winter season.",
    short: "Erie and Niagara equipment leaving Western New York ahead of the first heavy snow.",
    lede: [
      "Buffalo owners ship on a hard weather deadline. Equipment that is still sitting outside in December is equipment that is difficult to load, and Western New York snow can shut down a yard pickup for days at a time.",
      "We are a licensed broker and arrange your move with a vetted motor carrier. What we do here is match your unit's real dimensions and trailer condition to a truck that can legally and safely haul it.",
    ],
    context: [
      {
        heading: "The season closes fast",
        body: "Between the Lake Erie haul-out schedule and the first lake-effect events, the practical shipping season out of Buffalo is shorter than in most northern markets. Owners who wait until they are packed and ready are usually waiting behind everyone else.",
      },
      {
        heading: "Cross-border equipment adds paperwork",
        body: "Plenty of equipment in this market has Ontario ownership history or is stored on one side of the border and used on the other. If your unit is coming from Canada or has Canadian title paperwork, tell us up front — it changes the documentation, not just the routing.",
      },
      {
        heading: "Suburban and rural storage",
        body: "Boats and campers here commonly live in Hamburg, Orchard Park, Lancaster, Grand Island or out toward Chautauqua County. Rural pickups can be excellent for access but poor for turning radius; a photo of the driveway or lane saves a wasted dispatch.",
      },
    ],
    gear: [
      {
        name: "Lake Erie fishing boats and walleye rigs",
        note: "Usually trailered and straightforward, but old trailer bearings are the number one cause of trouble on a long southbound run.",
        equipment: "boat-transport",
      },
      {
        name: "Fifth wheels and toy haulers",
        note: "Owners heading to Florida or the Gulf Coast for the season often ship rather than tow across the mountains in winter.",
        equipment: "fifth-wheel-transport",
      },
      {
        name: "Snowmobiles going the other direction",
        note: "Not everything leaves. Some Buffalo customers ship sleds in from elsewhere; the same enclosed and open equipment options apply.",
        equipment: "atv-utv-transport",
      },
      {
        name: "Motorcycles",
        note: "Bikes typically ship in enclosed equipment or on a shared load, and are usually the easiest item on a multi-piece move.",
        equipment: "motorcycle-transport",
      },
    ],
    routes: [
      {
        label: "Buffalo → Fort Myers / Naples",
        note: "Runs I-90 to I-79 and down through the Carolinas. Delivery in Southwest Florida is normally a storage yard, marina or gated community.",
      },
      {
        label: "Buffalo → Tampa Bay",
        note: "The highest-frequency lane out of Western New York, which usually means a tighter pickup window than the less-traveled routes.",
      },
      {
        label: "Buffalo → Gulf Shores / Destin",
        note: "Popular with owners avoiding peninsula Florida traffic; fewer trucks run it, so give the schedule more room.",
      },
      {
        label: "Buffalo → Phoenix metro",
        note: "Long-haul and weather-sensitive across the middle of the country in late fall. Book early and plan on a multi-day pickup window.",
      },
    ],
    timing: [
      "Three to six weeks of lead time for October–December departures, and earlier if your unit is oversize or needs specialized equipment.",
      "March through May is the return season back into Western New York; April deliveries are usually easier to schedule than early March.",
      "Snow delays are real here. Building two or three flexible days into your pickup window costs nothing and prevents rescheduling.",
    ],
    logistics: [
      "Rural driveways and long lanes need to be checked before dispatch. Send a photo if there is any doubt about turning space.",
      "Winterize fully before pickup — this is a market where the first freeze often beats the truck.",
      "Have title, registration and any lien paperwork accessible if the unit has cross-border history.",
      "Roadworthiness at pickup is the owner's responsibility for anything rolling on its own tires, including tires, bearings, brakes and lights.",
    ],
    faqs: [
      {
        q: "How late in the year can you still pick up in Buffalo?",
        a: "Carriers run all winter, but access becomes the limiting factor once yards are snowed in. Shipping before mid-December avoids most of it.",
      },
      {
        q: "My boat is already shrink-wrapped. Is that a problem?",
        a: "Not usually, but wrapping adds height and can catch wind. Tell us it is wrapped so the dimensions we quote are the dimensions the driver sees.",
      },
      {
        q: "Do you cover Niagara Falls and the Southern Tier?",
        a: "Yes. We arrange pickups throughout Western New York, including Niagara County, Chautauqua County and the Southern Tier.",
      },
    ],
    equipment: [
      "boat-transport",
      "fifth-wheel-transport",
      "motorcycle-transport",
      "atv-utv-transport",
    ],
  },
  {
    slug: "madison",
    name: "Madison",
    stateName: "Wisconsin",
    stateSlug: "wisconsin",
    role: "origin",
    title: "Snowbird Toy Transport from Madison, WI | Enterprise Auto Transport",
    description:
      "Seasonal transport of pontoons, fishing boats, campers, fifth wheels and golf carts out of Madison and south-central Wisconsin to Florida, Texas and Arizona.",
    short: "Lake Mendota and Monona pontoons, campers and carts leaving before the freeze.",
    lede: [
      "Madison equipment is lake equipment. Pontoons and fishing boats come off Mendota, Monona and the Wisconsin River chain in the fall, and the owners who winter south want them out before the water starts locking up.",
      "Enterprise Auto Transport is a licensed broker. We match your unit to a carrier with the right trailer, and we tell you honestly when a piece of equipment needs specialized haul rather than a standard truck.",
    ],
    context: [
      {
        heading: "Pontoon beam is the local quoting issue",
        body: "Pontoons are everywhere in this market and many of them are wider than 8'6\" once the rub rail and fencing are measured. That single dimension is the difference between a routine load and a permitted oversize load, so measure the widest point rather than quoting the brochure spec.",
      },
      {
        heading: "One truck, several pieces",
        body: "It is common here for a household to move a boat, a golf cart and sometimes a side-by-side in the same season. Quoting them together usually beats booking three separate one-off moves, and it keeps everything arriving on the same schedule.",
      },
      {
        heading: "Storage is spread around the lakes",
        body: "Pickups happen from Sun Prairie, Middleton, Verona, Stoughton, DeForest and the Lake Wisconsin area at least as often as they happen at a Madison address. Confirm which yard the unit is actually sitting in before we dispatch.",
      },
    ],
    gear: [
      {
        name: "Pontoons and tritoons",
        note: "The signature boat of this market. Width, tower or bimini height and how the boat sits on its trailer all affect the equipment we need.",
        equipment: "boat-transport",
      },
      {
        name: "Travel trailers and campers",
        note: "Common for owners with a seasonal site in Florida or Texas. Age of the tires and bearings is the practical risk on a long southbound run.",
        equipment: "camper-transport",
      },
      {
        name: "Golf carts",
        note: "Carts headed to Florida and Arizona communities ship regularly from here, usually alongside other equipment.",
        equipment: "golf-cart-transport",
      },
      {
        name: "Boat trailers moving empty",
        note: "Some owners ship the boat and the trailer separately, or move an empty trailer to meet a boat already down south.",
        equipment: "boat-trailer-transport",
      },
    ],
    routes: [
      {
        label: "Madison → Fort Myers / Naples",
        note: "A steady seasonal lane. Delivery is usually into storage, a marina or a community with access rules worth confirming early.",
      },
      {
        label: "Madison → Tampa / Sarasota",
        note: "Frequently combined with Milwaukee and Chicago pickups, which helps availability in the peak weeks.",
      },
      {
        label: "Madison → Texas Gulf Coast",
        note: "Runs through Illinois and Missouri; a good option for owners wintering around Corpus Christi or the Rio Grande Valley.",
      },
      {
        label: "Madison → Phoenix / Tucson",
        note: "Fewer trucks per week than the Florida lanes. Build in more lead time and expect a broader pickup window.",
      },
    ],
    timing: [
      "October is the compressed month here. Three to six weeks of lead time is realistic; less than two weeks in peak season usually means accepting whatever window is available.",
      "March through May covers the return north, and late April is typically calmer than the first two weeks of March.",
      "If a boat still needs to be pulled and winterized, schedule that first and give us the date it will be ready — not the date you hope it will be.",
    ],
    logistics: [
      "Measure the widest and tallest points as the unit actually sits on the trailer, including any tower, arch or bimini frame.",
      "Drain and winterize before pickup; freeze damage in transit is not covered as a transport claim.",
      "Storage-lot pickups need gate access and a release contact if you have already headed south.",
      "Tires, bearings, brakes, lights and coupler must be roadworthy at pickup on anything that rolls, and roadside repairs or towing are the owner's responsibility.",
    ],
    faqs: [
      {
        q: "Is my pontoon too wide to ship normally?",
        a: "Many are. Anything over 8'6\" wide moves as an oversize load with permits and daylight-hours restrictions. It is routine work, but it needs to be quoted that way from the start.",
      },
      {
        q: "Can you move my boat and golf cart together?",
        a: "Often yes, depending on the trailer and the load plan. Tell us everything you want moved when you request the quote so we can price it as one job.",
      },
      {
        q: "Do you serve the Wisconsin Dells and Lake Wisconsin areas?",
        a: "Yes. We arrange pickups throughout south-central Wisconsin, including seasonal storage yards outside the Madison metro.",
      },
    ],
    equipment: [
      "boat-transport",
      "camper-transport",
      "golf-cart-transport",
      "boat-trailer-transport",
    ],
  },
  {
    slug: "columbus",
    name: "Columbus",
    stateName: "Ohio",
    stateSlug: "ohio",
    role: "origin",
    title: "Snowbird Toy Transport from Columbus, OH | Enterprise Auto Transport",
    description:
      "Boat, RV, travel trailer, golf cart and side-by-side transport out of Columbus and central Ohio to Florida, the Gulf Coast and Arizona.",
    short: "Central Ohio boats, campers and carts moving south on a reliable interstate lane.",
    lede: [
      "Columbus sits in one of the easier origin markets in the Midwest: it is centrally located, carriers pass through constantly on I-70 and I-71, and most pickups happen in suburbs with room to work. The scheduling pressure here comes from demand, not from difficult access.",
      "Enterprise Auto Transport is a licensed broker. We arrange your shipment with a qualified motor carrier and coordinate the details — we do not own trucks or employ drivers.",
    ],
    context: [
      {
        heading: "Good interstate access works in your favor",
        body: "Because I-70 and I-71 cross here, Columbus loads are easy for a driver to add to an existing southbound run. That generally means better pickup windows than markets further off the main corridors, especially outside the October peak.",
      },
      {
        heading: "Inland lake equipment, not coastal boats",
        body: "The boats in this market are mostly trailerable — Alum Creek, Hoover, Buckeye Lake and Indian Lake runabouts and pontoons, not big coastal cruisers. That keeps most moves on standard equipment, with pontoon width as the usual exception.",
      },
      {
        heading: "Suburban storage is the norm",
        body: "Dublin, Westerville, Delaware, Pickerington, Grove City and Hilliard yards handle most of the seasonal storage here. They load well, but they close on their own schedule — get the gate hours before we set the pickup.",
      },
    ],
    gear: [
      {
        name: "Runabouts, bowriders and pontoons",
        note: "Trailerable boats from the central Ohio reservoirs. Pontoon width and any tower height need to be measured, not estimated.",
        equipment: "boat-transport",
      },
      {
        name: "Travel trailers and fifth wheels",
        note: "Owners with seasonal sites in Florida or along the Gulf Coast often ship the trailer down and fly behind it.",
        equipment: "travel-trailer-transport",
      },
      {
        name: "Golf carts",
        note: "A steady seasonal item into Florida and Arizona communities. Tell us if it is lifted or has a rear seat kit, since that changes the footprint.",
        equipment: "golf-cart-transport",
      },
      {
        name: "Side-by-sides and ATVs",
        note: "Frequently shipped to winter riding areas in Arizona and Texas; often loaded with other equipment on the same truck.",
        equipment: "atv-utv-transport",
      },
    ],
    routes: [
      {
        label: "Columbus → Tampa / Sarasota",
        note: "High-frequency lane straight down I-75 corridor connections; usually the easiest Florida booking from here.",
      },
      {
        label: "Columbus → Naples / Fort Myers",
        note: "Southwest Florida delivery normally lands at storage or a gated address, both of which need confirming before dispatch.",
      },
      {
        label: "Columbus → Gulf Shores / Panhandle",
        note: "A shorter run than peninsula Florida and popular with owners who want less drive time when they visit.",
      },
      {
        label: "Columbus → Phoenix / Tucson",
        note: "Long-haul westbound with fewer weekly trucks; give it more lead time than a Florida move of the same size.",
      },
    ],
    timing: [
      "Three to six weeks of lead time covers October–December departures comfortably in this market.",
      "Spring returns run March through May; central Ohio deliveries in April are typically the smoothest of the season.",
      "Off-peak weeks in September and early October generally price better and dispatch faster than the last two weeks of October.",
    ],
    logistics: [
      "Confirm the storage yard's gate hours and whether the office needs a written release if you will not be present.",
      "Winterize before pickup. A boat or RV shipped with water in the lines can freeze on the first night out.",
      "Measure the unit on its trailer for width and height, including any tower, arch or A/C shroud.",
      "Roadworthy condition at pickup — tires, bearings, brakes, lights, coupler — is the owner's responsibility, as are towing and repairs if the unit is disabled in transit.",
    ],
    faqs: [
      {
        q: "How far in advance should I book from Columbus?",
        a: "Three to six weeks for the fall season. Columbus dispatches well because of the interstate crossings, but peak October weeks still fill up.",
      },
      {
        q: "Can you pick up in Delaware, Newark or Lancaster?",
        a: "Yes. We arrange pickups throughout the central Ohio region, not just inside the Columbus city limits.",
      },
      {
        q: "Do you ship the boat and the trailer as one unit?",
        a: "Usually yes, when the boat sits on its own roadworthy trailer. If the trailer is not fit for the highway, we quote it on carrier equipment instead.",
      },
    ],
    equipment: [
      "boat-transport",
      "travel-trailer-transport",
      "golf-cart-transport",
      "atv-utv-transport",
    ],
  },
  {
    slug: "pittsburgh",
    name: "Pittsburgh",
    stateName: "Pennsylvania",
    stateSlug: "pennsylvania",
    role: "origin",
    title: "Snowbird Toy Transport from Pittsburgh, PA | Enterprise Auto Transport",
    description:
      "Seasonal boat, RV, camper, motorcycle and golf cart transport out of Pittsburgh and Western Pennsylvania to Florida, the Carolinas and the Gulf Coast.",
    short: "River boats, campers and bikes leaving Western PA on the southbound corridors.",
    lede: [
      "Pittsburgh's recreational equipment is river equipment and mountain equipment: runabouts and cruisers off the three rivers, campers from the Laurel Highlands, and motorcycles that would rather spend February in Florida than in Allegheny County.",
      "We are a licensed broker. We arrange transportation with qualified motor carriers, verify the equipment fits the load, and stay on the shipment until it is delivered.",
    ],
    context: [
      {
        heading: "Terrain shapes the pickup, not the highway",
        body: "Hillside streets, narrow residential grades and tight alleys are a genuine constraint in this city. Many pickups here get relocated to a nearby lot or a flat commercial address simply because a full-length rig cannot safely work the street.",
      },
      {
        heading: "Marina season on the rivers",
        body: "Boats moored on the Allegheny, Monongahela or Ohio come out on a marina schedule. Coordinate the haul-out first and give us a ready date after it, rather than guessing at a date that depends on someone else's lift.",
      },
      {
        heading: "Corridor access is strong",
        body: "I-79, I-76 and I-70 all serve this market, so southbound trucks are available. Availability is rarely the issue here; access at the pickup address is.",
      },
    ],
    gear: [
      {
        name: "River cruisers and runabouts",
        note: "Length must include the swim platform and outboard. Anything with a tower or arch needs a real height measurement on the trailer.",
        equipment: "boat-transport",
      },
      {
        name: "Campers and travel trailers",
        note: "Laurel Highlands campers head south for the season regularly; tire age is the recurring issue on units that sat all summer.",
        equipment: "camper-transport",
      },
      {
        name: "Motorcycles",
        note: "A strong seasonal item from this market. Enclosed transport is the usual choice for touring and custom bikes.",
        equipment: "motorcycle-transport",
      },
      {
        name: "Jet skis and personal watercraft",
        note: "Often shipped on their own trailer or added to a larger move rather than booked alone.",
        equipment: "jet-ski-transport",
      },
    ],
    routes: [
      {
        label: "Pittsburgh → Tampa / Fort Myers",
        note: "The main seasonal lane out of Western PA, running down through Virginia and the Carolinas.",
      },
      {
        label: "Pittsburgh → Myrtle Beach / Hilton Head",
        note: "A shorter Carolinas run that fills quickly in October, particularly for campers and bikes.",
      },
      {
        label: "Pittsburgh → Gulf Shores / Destin",
        note: "Runs across Tennessee and Alabama; fewer trucks than the Florida lanes, so book with more room.",
      },
      {
        label: "Pittsburgh → Phoenix",
        note: "Long-haul westbound. Plan on a wider pickup window and earlier booking than an East Coast move.",
      },
    ],
    timing: [
      "Book three to six weeks out for October–December departures; the peak weeks tighten quickly across the whole Northeast at once.",
      "Return moves north run March through May, with April usually the easiest month to schedule.",
      "If your street is steep or narrow, plan an alternate loading location at booking time instead of on pickup day.",
    ],
    logistics: [
      "Send a photo of the street or driveway if there is any question about a full-size rig getting in and out.",
      "Marina and yard access hours need to be confirmed in writing when you will not be there.",
      "Winterize before pickup — Western PA nights turn cold well before the truck reaches warm weather.",
      "Owners are responsible for roadworthy tires, bearings, brakes, lights and couplers, and for towing or repairs if the unit fails on the road.",
    ],
    faqs: [
      {
        q: "My street is too narrow for a big truck. What happens?",
        a: "We arrange a nearby meeting point — a commercial lot, a wide side street or the storage yard. It is common here and it does not have to add cost when it is planned in advance.",
      },
      {
        q: "Can you pick up from a marina on the rivers?",
        a: "Yes, subject to the marina's access rules and lift schedule. Give us the haul-out date and the yard contact.",
      },
      {
        q: "Do you serve Washington, Butler and Westmoreland counties?",
        a: "Yes, along with the rest of Western Pennsylvania. Many pickups here happen outside the city proper.",
      },
    ],
    equipment: [
      "boat-transport",
      "camper-transport",
      "motorcycle-transport",
      "jet-ski-transport",
    ],
  },
  {
    slug: "traverse-city",
    name: "Traverse City",
    stateName: "Michigan",
    stateSlug: "michigan",
    role: "origin",
    title: "Snowbird Toy Transport from Traverse City, MI | Enterprise Auto Transport",
    description:
      "Northern Michigan seasonal transport: boats, cruisers, RVs and golf carts leaving Traverse City and Grand Traverse Bay for Florida and Arizona.",
    short: "Grand Traverse Bay boats and northern Michigan RVs heading out before winter.",
    lede: [
      "Traverse City is a summer market with a hard stop. Grand Traverse Bay boats come out in September and October, the resort communities empty, and a large amount of equipment leaves the region in a very short stretch of weeks.",
      "Enterprise Auto Transport is a licensed broker. We arrange the move with a carrier whose equipment fits your boat or RV and coordinate pickup around the marina and storage schedule.",
    ],
    context: [
      {
        heading: "A short, crowded shipping season",
        body: "Because the whole region winds down at once, the fall window here is narrower than in a large metro. Owners who wait until their marina calls them are usually calling us the same week as everyone else at that marina.",
      },
      {
        heading: "Bigger boats than most inland markets",
        body: "The Bay supports cruisers and sportfish boats that are genuinely oversize on a trailer. Beam and height often push these into permitted loads, which have their own routing and daylight restrictions.",
      },
      {
        heading: "Distance from the main corridors",
        body: "Northern Michigan is off the primary interstate lanes, so trucks are less spontaneous here than in Detroit or Grand Rapids. Lead time matters more, and combining pickups with other equipment in the area helps.",
      },
    ],
    gear: [
      {
        name: "Cruisers and sportfish boats",
        note: "Often over-width and over-height on the trailer. These need permitted transport and a measured height including arch, tower and radar.",
        equipment: "boat-transport",
      },
      {
        name: "Motorhomes and Class A coaches",
        note: "Some owners drive them south, others ship them. Shipping avoids winter mountain weather and puts no miles on the coach.",
        equipment: "motorhome-transport",
      },
      {
        name: "Golf carts",
        note: "Resort and second-home communities here send carts south every season; most move alongside other equipment.",
        equipment: "golf-cart-transport",
      },
      {
        name: "Jet skis and small watercraft",
        note: "Usually added to a larger move rather than shipped on their own, which keeps the cost per piece reasonable.",
        equipment: "jet-ski-transport",
      },
    ],
    routes: [
      {
        label: "Traverse City → Naples / Fort Myers",
        note: "Runs down through the Lower Peninsula and the I-75 corridor. The most requested lane out of northern Michigan.",
      },
      {
        label: "Traverse City → Sarasota / Bradenton",
        note: "Common for boat owners moving to a Gulf Coast slip; confirm marina delivery access before shipping.",
      },
      {
        label: "Traverse City → Phoenix / Scottsdale",
        note: "Long-haul westbound and thinly served from this region. Book several weeks ahead.",
      },
      {
        label: "Traverse City → Texas Gulf Coast",
        note: "Less common but regularly requested; usually routed with other Midwest pickups.",
      },
    ],
    timing: [
      "Start planning in August for an October departure. This market fills earlier than the larger Michigan metros.",
      "Oversize boats need the most lead time because permits and routing are arranged before the truck is scheduled.",
      "Spring returns land March through May, generally after the region thaws enough for a yard to be usable.",
    ],
    logistics: [
      "Marina haul-out dates drive everything — get the lift scheduled first, then book transport.",
      "Measure the boat as it sits on the trailer, including tower, arch and any electronics on top.",
      "Confirm winter access to the storage yard for the spring return; some yards are not plowed until late in the season.",
      "Roadworthiness of tires, bearings, brakes and lights is the owner's responsibility, along with towing or repair costs if the unit is disabled in transit.",
    ],
    faqs: [
      {
        q: "Is my boat too big to ship?",
        a: "Very few are. Wide or tall boats move as permitted oversize loads with specific routing and daylight rules. What matters is that the dimensions are accurate before dispatch.",
      },
      {
        q: "Why does northern Michigan need more lead time?",
        a: "There is less regular truck traffic here than in the southern part of the state, so we are scheduling a dedicated trip or coordinating with other pickups in the area.",
      },
      {
        q: "Do you cover Petoskey, Charlevoix and Elk Rapids?",
        a: "Yes. We arrange pickups throughout the Grand Traverse region and northern Lower Peninsula.",
      },
    ],
    equipment: [
      "boat-transport",
      "motorhome-transport",
      "golf-cart-transport",
      "jet-ski-transport",
    ],
  },
  {
    slug: "fort-wayne",
    name: "Fort Wayne",
    stateName: "Indiana",
    stateSlug: "indiana",
    role: "origin",
    title: "Snowbird Toy Transport from Fort Wayne, IN | Enterprise Auto Transport",
    description:
      "Boat, RV, travel trailer and golf cart transport out of Fort Wayne and northeast Indiana to Florida, Texas and Arizona for the winter season.",
    short: "Northeast Indiana lake boats and RV-country trailers heading south each fall.",
    lede: [
      "Northeast Indiana ships two very different things: lake equipment from the chain of lakes north of the city, and RVs from the manufacturing corridor around Elkhart and Goshen where a lot of this equipment was built in the first place.",
      "Enterprise Auto Transport is a licensed broker. We arrange your shipment with a qualified motor carrier and match the truck to your unit's real dimensions and trailer condition.",
    ],
    context: [
      {
        heading: "RV country changes expectations",
        body: "This region knows RVs, which means owners here often have accurate weights and dimensions on hand. Use them — an accurate spec sheet at quote time is the single biggest factor in a quote that does not change later.",
      },
      {
        heading: "Lake equipment from the north",
        body: "Lake James, Lake Wawasee, Sylvan Lake and the surrounding chain send pontoons and runabouts south every fall. Pontoon width is the usual permitting question, exactly as it is in Wisconsin and Michigan.",
      },
      {
        heading: "Straightforward highway access",
        body: "I-69 and US 30 keep this market reasonably well served by southbound trucks, and most pickups happen at suburban homes or storage yards with room to load.",
      },
    ],
    gear: [
      {
        name: "Travel trailers and fifth wheels",
        note: "Very common here. Age of the tires and bearings on a unit that has been parked all summer is the main pre-trip concern.",
        equipment: "fifth-wheel-transport",
      },
      {
        name: "Pontoons and runabouts",
        note: "From the northeast Indiana lakes. Width over 8'6\" moves as an oversize load and should be quoted that way from the start.",
        equipment: "boat-transport",
      },
      {
        name: "Golf carts",
        note: "Routinely shipped to Florida and Arizona communities, often on the same truck as a larger unit.",
        equipment: "golf-cart-transport",
      },
      {
        name: "Toy haulers",
        note: "Common in this market and often loaded with gear the owner wants to take along — which affects weight and needs to be declared.",
        equipment: "toy-hauler-transport",
      },
    ],
    routes: [
      {
        label: "Fort Wayne → Fort Myers / Naples",
        note: "A steady seasonal lane; Southwest Florida delivery usually means storage or a gated community address.",
      },
      {
        label: "Fort Wayne → Tampa / The Villages area",
        note: "Popular for both RVs and carts. Community delivery rules are worth confirming before the truck rolls.",
      },
      {
        label: "Fort Wayne → Texas Gulf Coast",
        note: "Runs south through Indianapolis and St. Louis; a good option for Rio Grande Valley and Corpus Christi winters.",
      },
      {
        label: "Fort Wayne → Phoenix / Yuma",
        note: "Long-haul westbound with fewer weekly trucks; give it extra lead time in October.",
      },
    ],
    timing: [
      "Three to six weeks of lead time for the October–December season; RVs and oversize boats want the longer end of that.",
      "March through May is the return window, and the first two weeks of March are the busiest.",
      "Declare any cargo loaded inside a toy hauler or RV at booking; weight changes both the truck and the quote.",
    ],
    logistics: [
      "Have the unit's actual weight and dimensions ready — in this market most owners do, and it makes quoting far more accurate.",
      "Winterize before pickup; a freeze on the first night out is a repair bill, not a claim.",
      "Storage yard pickups need gate hours and a release contact if you have already left for the season.",
      "Tires, bearings, brakes, lights and coupler must be roadworthy at pickup, and towing or repairs on the road are the owner's responsibility.",
    ],
    faqs: [
      {
        q: "Can I leave belongings inside my RV or toy hauler?",
        a: "Some weight is usually workable, but it must be declared and secured. Loose or undeclared cargo changes the weight the driver planned for and is not covered by cargo insurance.",
      },
      {
        q: "Do you serve Elkhart, Goshen and the lakes region?",
        a: "Yes. We arrange pickups throughout northeast Indiana, including the RV corridor and the northern lake communities.",
      },
      {
        q: "Is shipping an RV cheaper than driving it down?",
        a: "It depends on fuel, lodging and how much you value the miles you are not putting on the coach. We give you the transport number so you can compare honestly.",
      },
    ],
    equipment: [
      "fifth-wheel-transport",
      "boat-transport",
      "golf-cart-transport",
      "toy-hauler-transport",
    ],
  },
  {
    slug: "duluth",
    name: "Duluth",
    stateName: "Minnesota",
    stateSlug: "minnesota",
    role: "origin",
    title: "Snowbird Toy Transport from Duluth, MN | Enterprise Auto Transport",
    description:
      "Northern Minnesota seasonal transport of boats, campers, motorhomes and side-by-sides from Duluth and the North Shore to Florida, Texas and Arizona.",
    short: "North Shore boats and campers leaving early, because winter arrives early here.",
    lede: [
      "Duluth runs on an earlier calendar than the rest of Minnesota. Freeze risk starts sooner, marinas close sooner, and equipment that is not out by late October is often waiting for a break in the weather.",
      "Enterprise Auto Transport is a licensed broker. We arrange your move with a qualified motor carrier and are straightforward about what a northern pickup in late fall actually involves.",
    ],
    context: [
      {
        heading: "The earliest deadline in the state",
        body: "Superior's weather and the North Shore's exposure mean local yards and marinas wind down before the Twin Cities do. Plan on shipping a couple of weeks earlier than you would from Minneapolis.",
      },
      {
        heading: "Fewer trucks, more coordination",
        body: "This is a smaller market well north of the main lanes. Carriers usually build a Duluth pickup into a route that also serves the Twin Cities or Wisconsin, which is why lead time matters more here than price shopping does.",
      },
      {
        heading: "Working, weathered equipment",
        body: "A lot of North Shore boats and campers are used hard and stored outside. Trailer bearings, tires and lights on units like these deserve a genuine inspection before a 1,800-mile trip, not a quick glance.",
      },
    ],
    gear: [
      {
        name: "Deep-V fishing boats and walleye rigs",
        note: "Purpose-built lake boats, usually straightforward to haul once the trailer is confirmed roadworthy.",
        equipment: "boat-transport",
      },
      {
        name: "Campers and travel trailers",
        note: "Frequently older units that have sat outdoors. Tires and bearings are the first thing to check before booking.",
        equipment: "camper-transport",
      },
      {
        name: "Motorhomes",
        note: "Owners heading to Arizona or Texas for the winter often prefer shipping to driving a coach across the northern plains in November.",
        equipment: "motorhome-transport",
      },
      {
        name: "Side-by-sides and ATVs",
        note: "Common here and easy to add to a larger load headed to a winter riding area.",
        equipment: "atv-utv-transport",
      },
    ],
    routes: [
      {
        label: "Duluth → Phoenix / Tucson",
        note: "A long westbound run that is popular from northern Minnesota; book well ahead because trucks are limited.",
      },
      {
        label: "Duluth → Texas Gulf Coast",
        note: "Down through the Twin Cities and Missouri; frequently combined with other Minnesota pickups.",
      },
      {
        label: "Duluth → Fort Myers / Naples",
        note: "A full cross-country run to Southwest Florida. Confirm the delivery yard or community access early.",
      },
      {
        label: "Duluth → Tampa Bay",
        note: "Regularly requested; usually routed through Minneapolis and Chicago area pickups.",
      },
    ],
    timing: [
      "Aim for late September through mid-October. This is the market where waiting until November most often causes a problem.",
      "Four to six weeks of lead time is realistic given how few trucks serve the region weekly.",
      "Spring returns are typically April and May, once yards up here are usable again.",
    ],
    logistics: [
      "Winterize completely before pickup. Northern Minnesota nights are below freezing well before the season looks over.",
      "Have bearings and tires inspected on any trailer that has not made a long trip recently.",
      "Give us gate hours and a release contact for the storage yard if you are leaving before the truck arrives.",
      "Roadworthy condition at pickup is the owner's responsibility, and towing or roadside repair costs fall to the owner if the unit becomes disabled in transit.",
    ],
    faqs: [
      {
        q: "Why should I book earlier from Duluth than from Minneapolis?",
        a: "Two reasons: winter starts earlier here, and fewer trucks pass through weekly. Both push the practical booking date back several weeks.",
      },
      {
        q: "Can you pick up along the North Shore?",
        a: "Yes, subject to road and yard access. Tell us the exact location so we can confirm a full-size rig can get in and turn around.",
      },
      {
        q: "My camper is old. Will a carrier take it?",
        a: "Usually yes, if it is structurally sound and the trailer is roadworthy. If the tires or bearings are questionable, replace them first — a blowout on the road is the owner's cost.",
      },
    ],
    equipment: [
      "boat-transport",
      "camper-transport",
      "motorhome-transport",
      "atv-utv-transport",
    ],
  },
  {
    slug: "bradenton",
    name: "Bradenton",
    stateName: "Florida",
    stateSlug: "florida",
    role: "destination",
    title: "Snowbird Toy Transport to Bradenton, FL | Enterprise Auto Transport",
    description:
      "Seasonal delivery of boats, RVs, golf carts and trailers into Bradenton, Anna Maria Island and Lakewood Ranch — storage, marina and community access planning.",
    short: "Boats, carts and RVs arriving into Bradenton, Lakewood Ranch and the island communities.",
    lede: [
      "Bradenton takes in a mix of everything: boats headed for Manatee River and Gulf access slips, golf carts for the master-planned communities inland, and RVs for the seasonal parks around the county.",
      "Enterprise Auto Transport is based in Naples and works this coast constantly. Our job on a delivery is to make sure the receiving end — a slip, a lot, a gate — is actually ready before the truck is dispatched.",
    ],
    context: [
      {
        heading: "Two very different delivery zones",
        body: "Waterfront Bradenton and Anna Maria Island have narrow streets, bridge routes and limited turnaround space. Lakewood Ranch and the eastern communities are newer, wider and much easier for a full-size rig. Which side of the county you are on changes the delivery plan entirely.",
      },
      {
        heading: "Storage fills before the season does",
        body: "Boat and RV storage in Manatee County is finite and it books ahead of arrival season. Reserve the space before you ship; a truck arriving with nowhere to unload is the most expensive avoidable problem in this business.",
      },
      {
        heading: "Island access is a real constraint",
        body: "Anna Maria Island and Longboat Key deliveries often need to be staged on the mainland and moved the last few miles with smaller equipment. Plan for it rather than discovering it on delivery day.",
      },
    ],
    gear: [
      {
        name: "Boats headed for Gulf and river slips",
        note: "Confirm the marina will receive the boat and trailer, and whether they need a scheduled launch window.",
        equipment: "boat-transport",
      },
      {
        name: "Golf carts for Lakewood Ranch and similar communities",
        note: "Most communities here allow carts, but the delivery address still needs to be somewhere a transport truck can legally stop.",
        equipment: "golf-cart-transport",
      },
      {
        name: "RVs and travel trailers",
        note: "Deed restrictions commonly prohibit parking these at a residence, so delivery is usually to a storage facility or seasonal park.",
        equipment: "rv-transport",
      },
      {
        name: "Jet skis and small watercraft",
        note: "Often delivered with a larger unit; make sure the receiving storage allows them on the same lot.",
        equipment: "jet-ski-transport",
      },
    ],
    routes: [
      {
        label: "Midwest → Bradenton",
        note: "Chicago, Detroit, Milwaukee, Columbus and Indianapolis pickups all feed this market through the I-75 corridor.",
      },
      {
        label: "Northeast → Bradenton",
        note: "New York, Boston, Philadelphia and Pittsburgh loads run down the eastern corridors and cross over to the Gulf side.",
      },
      {
        label: "Bradenton → north, spring return",
        note: "March through May. Book the return before you fly home so pickup does not depend on you being here.",
      },
      {
        label: "Bradenton ↔ nearby Gulf markets",
        note: "Short repositioning moves to Sarasota, St. Petersburg or Punta Gorda when a slip or storage space changes.",
      },
    ],
    timing: [
      "Arrivals concentrate October through December, and the closer you are to the holidays the tighter delivery scheduling gets.",
      "Return trips north run March through May; the first half of March is the busiest stretch of the year on this coast.",
      "Have the storage or slip confirmed in writing before we dispatch, not while the truck is en route.",
    ],
    logistics: [
      "Give us the gate code, the office hours and a local contact who can accept delivery if you have not arrived yet.",
      "Island and waterfront deliveries may need a mainland staging point; tell us the address early so we can plan it.",
      "Check the storage facility's rules on trailers, fuel levels and whether they accept delivery from a commercial carrier.",
      "Inspect the unit at delivery and note anything on the bill of lading before the driver leaves — that document is the basis of any claim, and claims are filed with the carrier.",
    ],
    faqs: [
      {
        q: "Can you deliver to Anna Maria Island?",
        a: "Yes, though the last stretch sometimes requires smaller equipment or a mainland staging point because of street width and turnaround space. We plan that before dispatch.",
      },
      {
        q: "I have not arrived in Florida yet. Can someone else accept the delivery?",
        a: "Yes. Give us their name and number in advance, and make sure the storage facility or marina knows they are authorized to sign.",
      },
      {
        q: "Can my RV be parked at my Bradenton home?",
        a: "In many neighborhoods, no — deed restrictions are common. Most seasonal RVs here go to a storage facility or a seasonal park.",
      },
    ],
    equipment: [
      "boat-transport",
      "golf-cart-transport",
      "rv-transport",
      "jet-ski-transport",
    ],
  },
  {
    slug: "punta-gorda",
    name: "Punta Gorda",
    stateName: "Florida",
    stateSlug: "florida",
    role: "destination",
    title: "Snowbird Toy Transport to Punta Gorda, FL | Enterprise Auto Transport",
    description:
      "Delivering boats, RVs, golf carts and trailers into Punta Gorda, Port Charlotte and Charlotte Harbor — marina access, storage and canal-community delivery planning.",
    short: "Charlotte Harbor boats and Punta Gorda Isles carts and RVs arriving for the season.",
    lede: [
      "Punta Gorda is a boating destination first. Charlotte Harbor, the canal communities in Punta Gorda Isles and the marinas along the Peace River are why most equipment comes here, and boats are the majority of what we deliver into this market.",
      "We are a licensed broker based in Naples, one county south. This is home territory, and we know which yards and marinas in Charlotte County actually work well for a full-size transport rig.",
    ],
    context: [
      {
        heading: "Canal homes with tight approaches",
        body: "Punta Gorda Isles and the surrounding canal neighborhoods have narrow streets, cul-de-sacs and driveways that a 70-foot rig cannot use. Delivery is frequently staged at a nearby lot and finished with smaller equipment.",
      },
      {
        heading: "Marina capacity moves around",
        body: "Slip and dry-stack availability in Charlotte County shifts year to year. Confirm your space is held for the arrival date before the unit ships, and get the marina's receiving hours in writing.",
      },
      {
        heading: "RV parks are the usual RV destination",
        body: "As in most of Southwest Florida, residential RV parking is restricted in many communities. Seasonal parks and storage facilities around Port Charlotte and North Port take most of the arriving coaches and trailers.",
      },
    ],
    gear: [
      {
        name: "Center consoles and bay boats",
        note: "The dominant boat type here. Confirm whether the marina expects the boat on its trailer or wants it launched on arrival.",
        equipment: "boat-transport",
      },
      {
        name: "Golf carts",
        note: "Widely used in Punta Gorda Isles and nearby communities; delivery still needs an address where a truck can legally stop.",
        equipment: "golf-cart-transport",
      },
      {
        name: "RVs and fifth wheels",
        note: "Typically delivered to a seasonal park or storage facility rather than a residence.",
        equipment: "fifth-wheel-transport",
      },
      {
        name: "Boat trailers arriving separately",
        note: "Some owners keep a boat here and ship only the trailer, or the reverse. Both are routine when scheduled together.",
        equipment: "boat-trailer-transport",
      },
    ],
    routes: [
      {
        label: "Midwest → Punta Gorda",
        note: "Minneapolis, Milwaukee, Chicago and Michigan pickups feed this market heavily each fall.",
      },
      {
        label: "Northeast → Punta Gorda",
        note: "New York, New England and Pennsylvania loads run the eastern corridors into Southwest Florida.",
      },
      {
        label: "Punta Gorda ↔ Naples / Fort Myers",
        note: "Short in-region repositioning when storage, a slip or a residence changes between seasons.",
      },
      {
        label: "Punta Gorda → north, spring return",
        note: "March through May. Booking the return early is the difference between choosing your week and taking what is left.",
      },
    ],
    timing: [
      "October through December is arrival season, with December delivery scheduling complicated by holiday traffic.",
      "Return pickups run March through May and cluster hard in the first weeks of March.",
      "Reserve the slip, dry stack or storage space before shipping; capacity here is genuinely limited in peak season.",
    ],
    logistics: [
      "Canal-community deliveries often need a staging location. Tell us the exact street early so we can plan the last mile.",
      "Provide gate codes, marina receiving hours and a local contact authorized to accept the unit.",
      "Ask the marina whether they will accept a trailer on site or whether the trailer needs separate storage.",
      "Document the condition at delivery on the bill of lading before the driver leaves; claims are filed with the carrier, not with us, and that paperwork is what supports them.",
    ],
    faqs: [
      {
        q: "Can the truck deliver to my canal home in Punta Gorda Isles?",
        a: "Sometimes, but many of those streets cannot accommodate a full-size rig. We commonly stage nearby and finish the delivery with smaller equipment, which we arrange in advance.",
      },
      {
        q: "Do you deliver into Port Charlotte and North Port too?",
        a: "Yes, along with the rest of Charlotte County. Storage and seasonal parks there handle a large share of arriving RVs.",
      },
      {
        q: "Should the boat arrive on its trailer?",
        a: "That depends on your marina. Some want the boat on the trailer, some want it launched on arrival, and some cannot store a trailer at all. Confirm before you ship.",
      },
    ],
    equipment: [
      "boat-transport",
      "golf-cart-transport",
      "fifth-wheel-transport",
      "boat-trailer-transport",
    ],
  },
  {
    slug: "mesa",
    name: "Mesa",
    stateName: "Arizona",
    stateSlug: "arizona",
    role: "destination",
    title: "Snowbird Toy Transport to Mesa, AZ | Enterprise Auto Transport",
    description:
      "Seasonal delivery of RVs, park models, golf carts, side-by-sides and motorcycles into Mesa and the East Valley — resort park access and storage planning.",
    short: "RV resorts, carts and desert toys arriving into Mesa and the East Valley each winter.",
    lede: [
      "Mesa is one of the largest winter-visitor destinations in the country, and the equipment mix reflects it: RVs and park models for the resort communities, golf carts for getting around them, and side-by-sides for the desert.",
      "Enterprise Auto Transport is a licensed broker. On an Arizona delivery, most of our work is confirming that the resort or storage facility can receive your unit on the date the driver plans to arrive.",
    ],
    context: [
      {
        heading: "Resort parks have their own rules",
        body: "East Valley RV resorts commonly restrict delivery hours, require advance notice and control which gate a commercial truck may use. Get those rules from the park office and pass them to us before dispatch.",
      },
      {
        heading: "Age restrictions on RVs are common",
        body: "A number of Arizona resort communities enforce a rule on how old an RV can be. It has nothing to do with transport, but it can stop your unit at the gate — worth confirming with the park before you ship.",
      },
      {
        heading: "Desert toys arrive with the season",
        body: "Side-by-sides, ATVs and dirt bikes come into the Valley for winter riding. These load easily and are often combined with a cart or an RV on the same move.",
      },
    ],
    gear: [
      {
        name: "Motorhomes and Class A coaches",
        note: "Shipping avoids putting winter miles on the coach; the resort's delivery hours and gate access are the real scheduling factor.",
        equipment: "motorhome-transport",
      },
      {
        name: "Park models and travel trailers",
        note: "Park models are typically over-width and move as permitted loads with daylight-hours restrictions.",
        equipment: "travel-trailer-transport",
      },
      {
        name: "Golf carts",
        note: "Standard equipment in East Valley resort communities. Lifted carts and rear seat kits change the footprint, so mention them.",
        equipment: "golf-cart-transport",
      },
      {
        name: "Side-by-sides and ATVs",
        note: "Common winter arrivals for desert riding; usually easy to combine with another unit on the same truck.",
        equipment: "atv-utv-transport",
      },
    ],
    routes: [
      {
        label: "Upper Midwest → Mesa",
        note: "Minneapolis, Duluth, Milwaukee and the Dakotas feed this market heavily; a well-traveled but long westbound run.",
      },
      {
        label: "Great Lakes → Mesa",
        note: "Chicago, Michigan and Ohio pickups run west through the I-40 and I-80 corridors depending on season and weather.",
      },
      {
        label: "Northwest → Mesa",
        note: "Washington, Oregon and Idaho winter visitors ship south down the I-15 corridor.",
      },
      {
        label: "Mesa → north, spring return",
        note: "March through May, and April is usually easier to schedule than the March rush.",
      },
    ],
    timing: [
      "Arrivals concentrate October through December; January arrivals are possible but resort space is usually spoken for by then.",
      "Book three to six weeks ahead, and longer for park models or anything requiring permits.",
      "Spring departures run March through May; reserve the pickup before you fly home so it does not depend on you being present.",
    ],
    logistics: [
      "Get the resort's commercial delivery hours, gate and advance-notice requirements in writing before dispatch.",
      "Confirm any community age restriction on RVs before you ship, not after the unit arrives.",
      "Provide a local contact who can accept delivery and sign the bill of lading if you arrive later.",
      "Owners are responsible for roadworthy tires, bearings, brakes and lights, and for towing or repairs if the unit is disabled in transit.",
    ],
    faqs: [
      {
        q: "Can the carrier deliver directly inside my RV resort?",
        a: "Often yes, if the park allows commercial trucks and the site is accessible. Some parks require delivery at a staging area near the entrance instead.",
      },
      {
        q: "Is a park model shipped the same way as a travel trailer?",
        a: "No. Park models are usually over-width, which means permits, specific routing and daylight-only travel. They take more lead time to schedule.",
      },
      {
        q: "Do you deliver to Apache Junction, Gilbert and Chandler?",
        a: "Yes. We arrange deliveries throughout the East Valley and the greater Phoenix metro.",
      },
    ],
    equipment: [
      "motorhome-transport",
      "travel-trailer-transport",
      "golf-cart-transport",
      "atv-utv-transport",
    ],
  },
  {
    slug: "corpus-christi",
    name: "Corpus Christi",
    stateName: "Texas",
    stateSlug: "texas",
    role: "destination",
    title: "Snowbird Toy Transport to Corpus Christi, TX | Enterprise Auto Transport",
    description:
      "Winter Texan delivery of boats, RVs, travel trailers and golf carts into Corpus Christi, Port Aransas and the Coastal Bend.",
    short: "Coastal Bend arrivals: bay boats, RVs and carts for the Winter Texan season.",
    lede: [
      "The Coastal Bend draws Winter Texans from the northern plains and the upper Midwest, and the equipment that follows them is a mix of bay boats for the flats, RVs for the coastal parks, and carts for getting around Port Aransas and the island communities.",
      "Enterprise Auto Transport is a licensed broker. We arrange your delivery with a qualified carrier and work out the access details — park gates, marina hours, ferry constraints — before the truck is on the road.",
    ],
    context: [
      {
        heading: "Island access needs planning",
        body: "Port Aransas is reached by ferry or by the causeway from the south, and both affect how and when a large rig can deliver. Tell us the exact delivery address early; it changes routing more here than in most markets.",
      },
      {
        heading: "Coastal parks book ahead",
        body: "RV parks along the Coastal Bend fill for the Winter Texan season. Confirm your site is reserved and get the park's delivery rules before shipping — arriving without a confirmed site is the most common avoidable problem.",
      },
      {
        heading: "Salt air and storage choices",
        body: "Equipment stored uncovered near the coast weathers faster. If you are shipping something you care about, ask the storage facility what covered options exist before you commit to a season outdoors.",
      },
    ],
    gear: [
      {
        name: "Bay boats and flats boats",
        note: "The local boat of choice. Confirm whether your marina or park will store the trailer alongside the boat.",
        equipment: "boat-transport",
      },
      {
        name: "RVs and travel trailers",
        note: "The core of the Winter Texan season here; delivery is nearly always to a coastal park or storage facility.",
        equipment: "rv-transport",
      },
      {
        name: "Golf carts",
        note: "Heavily used in Port Aransas and the island communities. Mention lift kits or rear seats so the footprint is accurate.",
        equipment: "golf-cart-transport",
      },
      {
        name: "Jet skis and personal watercraft",
        note: "Usually added to a larger move rather than shipped alone, which keeps the per-piece cost down.",
        equipment: "jet-ski-transport",
      },
    ],
    routes: [
      {
        label: "Upper Midwest → Corpus Christi",
        note: "Minnesota, Wisconsin and the Dakotas run south through Kansas City and Dallas into the Coastal Bend.",
      },
      {
        label: "Great Lakes → Corpus Christi",
        note: "Chicago, Indiana and Ohio pickups route down through St. Louis and Dallas.",
      },
      {
        label: "Corpus Christi ↔ Rio Grande Valley",
        note: "Short repositioning moves to and from McAllen and the Valley when a park or storage arrangement changes.",
      },
      {
        label: "Corpus Christi → north, spring return",
        note: "March through May. Book the return before you leave so pickup does not wait on your travel schedule.",
      },
    ],
    timing: [
      "Arrivals run October through December, with the heaviest stretch just before the holidays.",
      "Three to six weeks of lead time is realistic; oversize units and anything needing permits want more.",
      "Departures north concentrate in March and early April, which is the tightest scheduling window of the Texas season.",
    ],
    logistics: [
      "Confirm the park site or storage space in writing before we dispatch.",
      "Give us the park's commercial delivery hours and gate requirements, plus a contact who can sign if you have not arrived.",
      "For Port Aransas addresses, tell us early — ferry and causeway routing affects which equipment can deliver and when.",
      "Roadworthiness at pickup is the owner's responsibility for anything rolling on its own tires, and towing or repair costs on the road fall to the owner.",
    ],
    faqs: [
      {
        q: "Can you deliver on Mustang Island or in Port Aransas?",
        a: "Yes, with planning. Access is by ferry or causeway, so we confirm routing and sometimes stage on the mainland for the final leg.",
      },
      {
        q: "Do I need my RV park site reserved before shipping?",
        a: "Yes. Coastal Bend parks fill for the season, and a truck arriving without a confirmed site creates storage costs that are easy to avoid.",
      },
      {
        q: "Do you also serve Rockport and Aransas Pass?",
        a: "Yes. We arrange deliveries throughout the Coastal Bend, including the smaller bay communities.",
      },
    ],
    equipment: [
      "boat-transport",
      "rv-transport",
      "golf-cart-transport",
      "jet-ski-transport",
    ],
  },
];
